import PropTypes from 'prop-types'
import { CURRENT_ACTIONS } from '../actions/types'

/**
 * @typedef {Object} CurrentAgencyProperties
 * @property {number|null} account_rep_admin_id - The ID of the account representative admin, or null if not assigned.
 * @property {number} organization_id - The ID of the organization associated with the agency.
 * @property {string} created_at - The creation timestamp in ISO 8601 format.
 * @property {string} updated_at - The update timestamp in ISO 8601 format.
 * @property {Object} agency_logo - The agency logo information.
 * @property {string|null} agency_logo.url - The URL of the main agency logo, or null if not available.
 * @property {Object} agency_logo.small - The small-sized version of the agency logo.
 * @property {string|null} agency_logo.small.url - The URL of the small-sized logo, or null if not available.
 * @property {Object} agency_logo.medium - The medium-sized version of the agency logo.
 * @property {string|null} agency_logo.medium.url - The URL of the medium-sized logo, or null if not available.
 * @property {Object} agency_logo.initial - The initial-sized version of the agency logo.
 * @property {string|null} agency_logo.initial.url - The URL of the initial-sized logo, or null if not available.
 * @property {string|null} discarded_at - The timestamp when the agency was discarded, in ISO 8601 format, or null.
 */

/**
 * @typedef {import('./agenciesReducer').Agency & CurrentAgencyProperties} CurrentAgency
 */

export const currentAgencyPropTypes = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  organization_id: PropTypes.number,
  created_at: PropTypes.string,
  updated_at: PropTypes.string,
  agency_logo: PropTypes.shape({
    url: PropTypes.string,
    small: PropTypes.shape({
      url: PropTypes.string
    }),
    medium: PropTypes.shape({
      url: PropTypes.string
    }),
    initial: PropTypes.shape({
      url: PropTypes.string
    })
  }),
  account_status: PropTypes.string,
  account_rep_admin_id: PropTypes.number,
  agency_plan_id: PropTypes.number,
  hubspot_link: PropTypes.string,
  owner_name: PropTypes.string,
  owner_email: PropTypes.string,
  discarded_at: PropTypes.string,
  maintenance_mode: PropTypes.bool
})

/**
 * @typedef {Object} CurrentOrganization
 * @property {number} id - The unique identifier of the organization.
 * @property {string} name - The name of the organization.
 * @property {string} created_at - The creation timestamp in ISO 8601 format.
 * @property {string} updated_at - The update timestamp in ISO 8601 format.
 */

export const currentOrganizationPropTypes = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  created_at: PropTypes.string,
  updated_at: PropTypes.string
})

/**
 * @typedef {Object} CurrentState
 * @property {CurrentAgency|null} agency - The current agency, or null if not set.
 * @property {CurrentOrganization|null} organization - The current organization, or null if not set.
 */

export const currentPropTypes = PropTypes.shape({
  agency: currentAgencyPropTypes,
  organization: currentOrganizationPropTypes
})

/** @type {CurrentState} */
const initialState = {
  // TODO: admin: {},
  agency: null,
  organization: null
}

/**
 * @param {CurrentState} state - The current state.
 * @param {{ type: string, payload: CurrentAgency|CurrentOrganization }} action - The action to apply.
 *
 * @returns {CurrentState} The new state.
 */
export default (state = initialState, { type, payload }) => {
  switch (type) {
    case CURRENT_ACTIONS.SET_ORGANIZATION:
      return { agency: null, organization: payload }
    case CURRENT_ACTIONS.SET_AGENCY:
      return { organization: null, agency: payload }
    case CURRENT_ACTIONS.CLEAR_AGENCY:
      // sessionStorage.removeItem('agencyAuthToken')
      return { ...state, agency: null }
    default:
      return state
  }
}
